import './FreetextAnnotation.css'
import React from 'react'
import classnames from 'classnames'
import formatMessage from 'format-message'
import FreetextEntry from './FreetextEntry'
import SecondaryControls from '../../SecondaryControls'
import Tooltip from '../Tooltip'
import GenericPopup from '../../GenericPopup'
import CommentLine from '../../comments/CommentLine'
import CommentsContainer from '../../comments/CommentsContainer'
import { DEFAULT_SCALE, DEFAULT_FREETEXT_ANNOTATION_COLOR } from '../../constants'
import {
  focusOnNextAnnotation,
  getRelatedTargetOrEquivalent,
  getTranslatedCordinates,
} from '../utils'
import { isPenEvent, isTouchEvent } from '../../utils'

export default class FreetextAnnotation extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      isHovered: false,
      isDeletePopupVisible: false,
      isDragging: false,
      isMouseInTextarea: false,
      isTabPressed: false,
      translateX: 0,
      translateY: 0,
      scaleModifier: 1,
    }
  }

  setDivRef = (node) => {
    this.div = node
  }

  componentDidMount() {
    this.scaleFreetext(this.props.viewportScale)
    this.div.addEventListener('focusout', this.handleFreetextGroupBlur, true)
  }

  componentWillUnmount() {
    this.div.removeEventListener('focusout', this.handleFreetextGroupBlur, true)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.viewportScale !== this.props.viewportScale) {
      this.scaleFreetext(nextProps.viewportScale)
    }
  }

  scaleFreetext = (scale) => {
    const scaledWidth = (this.div.clientWidth * scale) / DEFAULT_SCALE
    const scaleModifier = scaledWidth / this.div.clientWidth

    this.setState((state) => ({ ...state, scaleModifier }))
  }

  handleFocusOut = (event) => {
    const relatedTarget = getRelatedTargetOrEquivalent(event)
    return (
      (this.div && this.div.contains(relatedTarget)) ||
      (this.props.commentsContainerRef &&
        this.props.commentsContainerRef.current &&
        this.props.commentsContainerRef.current.contains(relatedTarget))
    )
  }

  handleCommentGroupBlur = (event) => {
    const relatedTarget = event.relatedTarget
    const isRelatedTargetInsideAnnotation =
      (this.div && this.div.contains(relatedTarget)) ||
      (this.props.commentsContainerRef &&
        this.props.commentsContainerRef.current &&
        this.props.commentsContainerRef.current.contains(relatedTarget))

    if (isRelatedTargetInsideAnnotation === false) {
      this.props.onDeselectAnnotation(this.props.annotation.id)
    }
  }

  handleFreetextGroupBlur = (event) => {
    const isTabPressed = this.state.isTabPressed
    const shiftAndTabPressed = isTabPressed && this.props.isShiftPressed
    const isRelatedTargetInsideAnnotation = this.handleFocusOut(event)
    if (isRelatedTargetInsideAnnotation === false) {
      this.props.onDeselectAnnotation(this.props.annotation.id)
    }

    if (isTabPressed && shiftAndTabPressed === false && isRelatedTargetInsideAnnotation === false) {
      focusOnNextAnnotation(this.props.ySortedStagedRootAnnotations, this.props.annotation.id)
    }

    const isLastAnnotation =
      this.props.ySortedStagedRootAnnotations[this.props.ySortedStagedRootAnnotations.length - 1]
        .id === this.props.annotation.id

    const shouldTransferFocusToNextPage =
      isLastAnnotation &&
      isTabPressed &&
      this.props.isShiftPressed === false &&
      isRelatedTargetInsideAnnotation === false

    const isFirstAnnotation =
      this.props.ySortedStagedRootAnnotations[0].id === this.props.annotation.id

    const shouldTransferFocusToPreviousPage =
      isFirstAnnotation &&
      shiftAndTabPressed &&
      (event.target.className?.includes('FreetextGroup') ||
        event.target.className?.includes('Freetext-textarea'))

    if (shouldTransferFocusToNextPage) {
      // notify Viewer.js that we need to transfer focus to the next page.
      event.preventDefault()
      event.stopPropagation()

      this.props.onDeselectAnnotation(this.props.annotation.id)
      this.props.handleNextPageFocus()
      return
    } else if (shouldTransferFocusToPreviousPage) {
      event.preventDefault()
      event.stopPropagation()
      this.props.onDeselectAnnotation(this.props.annotation.id)
      this.props.handlePreviousPageFocus()
    }
  }

  handleKeyDown = (event) => {
    if (
      document.activeElement.className?.includes('FreetextGroup') &&
      this.props.canDelete &&
      (event.key === 'Delete' || event.key === 'Backspace')
    ) {
      this.handleDelete()
      return
    }
    if (document.activeElement.className?.includes('FreetextGroup') && this.props.canEdit) {
      switch (event.key) {
        case 'Escape':
          this.div.blur()
          break
        case 'ArrowRight':
          this.handleKeyPressMove(event.shiftKey ? 1 : 5, 0)
          event.preventDefault()
          return
        case 'ArrowLeft':
          this.handleKeyPressMove(event.shiftKey ? -1 : -5, 0)
          event.preventDefault()
          return
        case 'ArrowUp':
          this.handleKeyPressMove(0, event.shiftKey ? -1 : -5)
          event.preventDefault()
          return
        case 'ArrowDown':
          this.handleKeyPressMove(0, event.shiftKey ? 1 : 5)
          event.preventDefault()
          return
        default:
          break
      }
    }

    if (event.key === 'Tab') {
      if (event.persist) {
        event.persist()
      }
      this.setState(
        {
          isTabPressed: true,
        },
        () => {
          const deleteAnnotationButton = this.div.querySelector('.Delete-annotation-button')
          const canShiftTabToPreviousAnnotation =
            document.activeElement.className?.includes('Freetext-textarea') ||
            document.activeElement.className?.includes('FreetextGroup') ||
            (this.props.canDelete &&
              this.props.canEdit === false &&
              document.activeElement === deleteAnnotationButton)
          if (
            this.props.isShiftPressed &&
            this.div.contains(document.activeElement) &&
            canShiftTabToPreviousAnnotation
          ) {
            const previousAnnotationIndex =
              this.props.ySortedStagedRootAnnotations.findIndex(
                (annotation) => annotation.id === this.props.annotation.id,
              ) - 1
            if (previousAnnotationIndex >= 0) {
              const previousAnnotation =
                this.props.ySortedStagedRootAnnotations[previousAnnotationIndex]

              const previousAnnotationIsDeletedFreetextAnnotation =
                previousAnnotation.type === 'freetext' && previousAnnotation.deleted

              const previousAnnotationIsUneditableFreetextAnnotation =
                previousAnnotation.type === 'freetext' && this.props.canEdit

              if (previousAnnotationIsUneditableFreetextAnnotation) {
                event.preventDefault()
                document.querySelector(`#Annotation-${previousAnnotation.id}`).focus()
              } else if (previousAnnotationIsDeletedFreetextAnnotation) {
                const acknowledgeDeleteButton = document.querySelector(
                  `#Annotation-${previousAnnotation.id}-comment-group .Acknowledge-remove-deleted-comment-button`,
                )
                event.preventDefault()
                acknowledgeDeleteButton.focus()
              } else if (previousAnnotation.type !== 'freetext') {
                const previousAnnotationCommentGroup = document.querySelector(
                  `#Annotation-${previousAnnotation.id}-comment-group`,
                )
                if (previousAnnotationCommentGroup) {
                  event.preventDefault()
                  const textareas = previousAnnotationCommentGroup.querySelectorAll('textarea')
                  const lastTextarea = textareas[textareas.length - 1]
                  if (lastTextarea) {
                    lastTextarea.focus()
                  } else {
                    document
                      .querySelector(
                        `#Annotation-${previousAnnotation.id} .Annotation-svg-container `,
                      )
                      .focus()
                  }
                }
              }
              return
            }
          }
          if (this.props.annotation.deleted) {
            if (event.persist) {
              event.persist()
            }
            const acknowledgeDeleteButton = this.props.commentsContainerRef.current.querySelector(
              '.Acknowledge-remove-deleted-comment-button',
            )
            const acknowledgeDeleteButtonIsFocused =
              document.activeElement === acknowledgeDeleteButton

            const activeElementIsFreetextGroup =
              document.activeElement.className?.includes('FreetextGroup') ||
              document.activeElement.className?.includes('Freetext-textarea')

            const hasNextAnnotation =
              this.props.ySortedStagedRootAnnotations[
                this.props.ySortedStagedRootAnnotations.length - 1
              ].id !== this.props.annotation.id

            if (activeElementIsFreetextGroup && this.props.isShiftPressed === false) {
              this.props.commentsContainerRef.current.querySelector('.CommentGroup').focus()
            } else if (
              acknowledgeDeleteButtonIsFocused &&
              hasNextAnnotation &&
              this.props.isShiftPressed === false
            ) {
              event.preventDefault()
              focusOnNextAnnotation(
                this.props.ySortedStagedRootAnnotations,
                this.props.annotation.id,
              )
            } else if (acknowledgeDeleteButtonIsFocused && this.props.isShiftPressed) {
              event.preventDefault()
              this.props.onSelectAnnotation(this.props.annotation.id)
              this.div.querySelector('.FreetextGroup').focus()
            }
            return
          }
        },
      )
    }
  }

  handleKeyPressMove = (x, y) => {
    this.moveAnnotation(this.state.translateX + x, this.state.translateY + y)
  }

  handleMouseDown = (event) => {
    if (this.state.isMouseInTextarea || !this.props.canEdit || !this.div.contains(event.target)) {
      return
    }
    if (isPenEvent(event)) {
      this.props.onIsPenAnnotatingChange(true)
    }

    this.isTouchDown = isTouchEvent(event) // Store if this interaction started with touch

    if (this.isTouchDown) {
      event.preventDefault() // Prevent default touch behavior like scrolling
    }

    this.props.onTextSelectionDisable()
    this.originalPageX = event.pageX
    this.originalPageY = event.pageY
    this.originalTranslateX = this.state.translateX
    this.originalTranslateY = this.state.translateY
    this.setState((state) => ({
      ...state,
      isDragging: true,
      isSelected: true,
    }))
    this.props.onDraggingStart(this.props.annotation.id)
    document.addEventListener('pointerup', this.handleDocumentMouseUp)
    document.addEventListener('pointermove', this.handleDocumentMouseMove)
  }

  handleOnClick = (_event) => {
    if (!this.props.isSelected) {
      this.props.onSelectAnnotation(this.props.annotation.id)
    }
  }

  handleDocumentMouseMove = (event) => {
    if (!this.state.isDragging || !this.props.annotation.contents) return

    if (this.isTouchDown) {
      event.preventDefault() // Prevent scrolling if dragging via touch
    }

    const { translateX, translateY } = getTranslatedCordinates({
      pageX: event.pageX,
      pageY: event.pageY,
      originalPageX: this.originalPageX,
      originalPageY: this.originalPageY,
      viewportHeight: this.props.viewportHeight,
      viewportWidth: this.props.viewportWidth,
      x: this.props.x,
      y: this.props.y,
    })
    this.moveAnnotation(translateX, translateY)
  }

  handlePointerMove = (event) => {
    if (isPenEvent(event)) {
      this.props.onIsPenAnnotatingChange(true)
    }
  }

  moveAnnotation = (x, y) => {
    const x1 = this.props.annotation.rect[0][0] + x
    const y1 = this.props.annotation.rect[0][1] + y
    const y2 = this.props.annotation.rect[1][1] + y
    const freetextHeightPadding = 10
    const freetextMinWidth = 40 * this.props.viewportScale

    const translatedX =
      x1 + freetextMinWidth < this.props.viewportWidth && x1 > 0 ? x : this.state.translateX
    const translatedY =
      y1 + freetextHeightPadding < this.props.viewportHeight && y2 > 0 ? y : this.state.translateY

    this.setState((state) => ({
      ...state,
      translateX: translatedX,
      translateY: translatedY,
    }))
  }

  handleCommentDelete = (annotationId, isRootComment = false) => {
    this.props.onDeleteAnnotation(this.props.annotation.page, annotationId, isRootComment)
  }

  handleDocumentMouseUp = (event) => {
    this.props.onIsPenAnnotatingChange(false)
    if (event.key && event.key === 'Tab') {
      this.setState({ isTabPressed: false })
    }
    if (!this.state.isDragging) return

    this.props.onTextSelectionEnable()
    if (this.state.isDragging) {
      this.setState(
        (state) => ({ ...state, isDragging: false }),
        () => {
          if (this.state.translateX !== 0 || this.state.translateY !== 0) {
            this.props.onMoveAnnotation(
              this.props.annotation,
              this.state.translateX,
              this.state.translateY,
            )
            this.setState((state) => ({
              ...state,
              translateX: 0,
              translateY: 0,
            }))
          }
        },
      )
      this.props.onDraggingEnd()
    }
    document.removeEventListener('pointerup', this.handleDocumentMouseUp)
    document.removeEventListener('pointermove', this.handleDocumentMouseMove)
  }

  handleAnnotationSave = (updatedAnnotationContents, { clientHeight, clientWidth }) => {
    const annotation = this.props.annotation
    const [[x1, y1], [x2, y2]] = annotation.rect // eslint-disable-line

    // logic will change when we allow moving of freetext annotations or
    // modifying annotation dimensions

    this.handleCommentSave(this.props.annotation.id, updatedAnnotationContents, [
      [x1, y2 + clientHeight],
      [x1 + clientWidth, y2],
    ])
  }

  handleCommentSave = (annotationId, contents, rect) => {
    const baseAnnotation = this.props.annotation
    const commentReplies = [] // assign to this.props.commentReplies when supported
    const annotationComments = [...commentReplies, baseAnnotation]
    const existingAnnotation = annotationComments.find(
      (annotation) => annotation.id === annotationId,
    )

    if (contents && contents.trim().length) {
      if (typeof baseAnnotation.created_at === 'string') {
        this.props.onUpdateAnnotation(this.props.viewportHeight, this.props.viewportScale, {
          ...existingAnnotation,
          contents,
          rect,
        })
      } else {
        this.props.onCreateAnnotation(this.props.viewportHeight, this.props.viewportScale, {
          ...baseAnnotation,
          contents,
          rect,
        })
      }
    }
  }

  handleDelete = () => {
    if (this.props.canDelete) {
      this.setState((state) => ({ ...state, isDeletePopupVisible: true }))
    }
  }

  handleDeletePopupConfirm = () => {
    this.setState((state) => ({ ...state, isDeletePopupVisible: false }))
    const annotation = this.props.annotation
    if (this.props.canDelete) {
      this.props.onDeleteAnnotation(annotation.page, annotation.id)
    }
  }

  handleDeletePopupCancel = () => {
    this.setState((state) => ({ ...state, isDeletePopupVisible: false }))
    this.div.focus()
    this.props.onHoverAnnotation(null)
  }

  handleisMouseInTextarea = (isMouseInTextarea) => {
    this.setState((state) => ({ ...state, isMouseInTextarea }))
  }

  handleMouseEnter = (_event) => {
    this.setState((state) => ({ ...state, isHovered: true }))
    this.props.onHoverAnnotation(this.props.annotation.id)
  }

  handleMouseLeave = (_event) => {
    this.setState((state) => ({ ...state, isHovered: false }))
    this.props.onHoverAnnotation(null)
  }

  handleAnnotationFocus = (event) => {
    event.persist()
    event.preventDefault()
    event.stopPropagation()
    this.props.onSelectAnnotation(this.props.annotation.id)
  }

  scaleRect = (rect, oldFont, newFont) => {
    // Add a fix for existing annotations that were scaled without their rect updated
    // Maybe check if height/width are greater/smaller than they should be, for the given font size
    // Should this happen in GET annotations, as a data fixup?  Probably
    const oldFontSize = oldFont.split(' ')[0].replace(/\D/g, '')
    const newFontSize = newFont.split(' ')[0].replace(/\D/g, '')
    const scaleFactor = newFontSize / oldFontSize
    const width = rect[1][0] - rect[0][0]
    const height = rect[1][1] - rect[0][1]
    const scaledWidth = width * scaleFactor
    const scaledHeight = height * scaleFactor
    const newLowerX = rect[0][0] + scaledWidth
    const newLowerY = rect[0][1] + scaledHeight
    const scaledRect = [rect[0], [newLowerX, newLowerY]]
    return scaledRect
  }

  updateColor = (color) => {
    this.props.onUpdateAnnotation(this.props.viewportHeight, this.props.viewportScale, {
      ...this.props.annotation,
      color,
    })
    this.props.onCurrentColorChange(color, 'freetext')
  }

  updateBgColor = (bgColor) => {
    this.props.onUpdateAnnotation(this.props.viewportHeight, this.props.viewportScale, {
      ...this.props.annotation,
      bgColor,
    })
    this.props.onCurrentBgColorChange(bgColor, 'freetext')
  }

  updateTextSize = (font) => {
    const scaledRect = this.scaleRect(this.props.annotation.rect, this.props.annotation.font, font)

    this.props.onUpdateAnnotation(this.props.viewportHeight, this.props.viewportScale, {
      ...this.props.annotation,
      font,
      rect: scaledRect,
    })
    this.props.onCurrentTextSizeChange(font, 'freetext')
  }

  render() {
    const annotation = this.props.annotation
    const [[x1, y1], [x2, y2]] = this.props.annotation.rect // eslint-disable-line
    const isActive =
      (this.props.isSelected && this.div && this.div.contains(document.activeElement)) ||
      this.state.isDragging

    const freetextGroupClassnames = classnames('FreetextGroup', {
      'FreetextGroup--active': isActive,
      'FreetextGroup--editable': this.props.canEdit,
    })
    const textSize = annotation.font && annotation.font.split(' ')[0].replace(/\D/g, '')

    const fontSize = textSize || '10'
    const x = x1 + this.state.translateX
    const y = y2 + this.state.translateY

    if (this.state.hasError) {
      return (
        <div
          style={{
            left: x,
            top: y,
          }}
        >
          Error
        </div>
      )
    }

    const showSecondaryControls = this.props.isSelected && this.props.canEdit
    const deleteMessage = formatMessage('Are you sure you want to delete this annotation?')

    const rect = this.props.annotation.rect.map((point) => {
      const [x, y] = point
      return [x + 5, y + 5]
    })

    const showCommentLine =
      this.props.annotation.deleted &&
      (this.props.isHovered || this.props.isSelected) &&
      !this.state.isDragging

    const annotationClassnames = classnames('Annotation', {
      'Annotation--selected': this.props.isSelected,
      'Annotation--is-dragging': this.state.isDragging,
    })

    const freetextContainerStyle = {
      touchAction: this.state.isDragging ? 'none' : 'auto', // Only prevent scrolling when dragging
    }

    return (
      <div
        key={this.props.annotation.id + '-container'}
        className={annotationClassnames}
        role="button"
        aria-label={formatMessage('Move Annotation')}
        tabIndex={-1}
        onClick={this.handleOnClick}
        onFocus={this.handleAnnotationFocus}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.handleDocumentMouseUp}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        ref={this.setDivRef}
        style={freetextContainerStyle}
      >
        <div
          key={`Annotation-${this.props.annotation.id}`}
          id={`Annotation-${this.props.annotation.id}`}
          className={freetextGroupClassnames}
          style={{
            left: x,
            top: y,
            backgroundColor: this.props.annotation.bgColor,
            border: 'transparent',
            cursor:
              typeof this.props.annotation.contents === 'string' &&
              this.props.annotation.contents.length > 0
                ? 'move'
                : 'auto', //TEMPORARY
          }}
          tabIndex={-1}
          onPointerDown={this.handleMouseDown}
          onPointerMove={this.handlePointerMove}
        >
          <FreetextEntry
            annotationContents={annotation.contents}
            annotationPage={annotation.page}
            annotationID={annotation.id}
            annotationFontSize={fontSize}
            annotationColor={annotation.color}
            annotationFillColor={annotation.bgColor}
            annotationUserName={annotation.user_name}
            annotationWidth={x2 - x1}
            isSelected={this.props.isSelected}
            isDragging={this.state.isDragging}
            canEdit={this.props.canEdit}
            x={x}
            y={y}
            scaleModifier={this.state.scaleModifier}
            viewportWidth={this.props.viewportWidth}
            viewportHeight={this.props.viewportHeight}
            onParentBlur={this.handleFocusOut}
            onSelectAnnotation={this.props.onSelectAnnotation}
            onDeleteAnnotation={this.props.onDeleteAnnotation}
            onAnnotationSave={this.handleAnnotationSave}
            onTextareaMouseEnter={this.handleisMouseInTextarea}
          />
          <div className="FreetextTooltip-container">
            {isActive && (
              <div className="FreetextTooltip">
                <Tooltip
                  canEdit={this.props.canEdit}
                  canDelete={this.props.canDelete}
                  displayAuthorName={!this.props.canEdit}
                  onDeleteAnnotation={this.handleDelete}
                  authorName={this.props.annotation.user_name}
                />
              </div>
            )}
          </div>
        </div>
        {showCommentLine && (
          <CommentLine
            key={this.props.annotation.id}
            annotationId={this.props.annotation.id}
            currentScrollTop={this.props.currentScrollTop}
            isAutomaticallyScrollingComments={this.props.isAutomaticallyScrollingComments}
            isSelected={this.props.isSelected}
            onSelectAnnotation={this.props.onSelectAnnotation}
            managedTop={this.props.managedTop}
            pageHeight={this.props.pageHeight}
            rect={rect}
            viewportWidth={this.props.viewportWidth}
            widthExtension={24}
          />
        )}

        {this.props.annotation.deleted &&
          this.props.commentsContainerRef &&
          this.props.commentsContainerRef.current && (
            <CommentsContainer
              annotation={this.props.annotation}
              annotationId={this.props.annotation.id}
              canEdit={false}
              iconColor={annotation.color ? annotation.color : DEFAULT_FREETEXT_ANNOTATION_COLOR}
              commentReplies={this.props.commentReplies}
              commentsContainerRef={this.props.commentsContainerRef}
              isAnnotatable={this.props.isAnnotatable}
              isCommenting={false}
              isHovered={this.props.isHovered}
              isSelected={this.props.isSelected}
              managedTop={this.props.managedTop}
              onCommentBlur={null}
              onCommentGroupBlur={this.handleCommentGroupBlur}
              onCommentGroupHeightChange={this.props.onCommentGroupHeightChange}
              onCommentGroupUnmount={this.props.onCommentGroupUnmount}
              onCommentFocus={this.handleCommentsContainerFocus}
              onCommentSave={this.props.onCommentSave}
              onDeleteAnnotation={this.handleCommentDelete}
              onHoverAnnotation={this.props.onHoverAnnotation}
              onSelectComment={this.setCommentState}
              pageHeight={this.props.pageHeight}
              rect={this.props.annotation.rect}
              sessionUserObject={this.props.sessionUserObject}
              viewportHeight={this.props.viewportHeight}
              viewportScale={this.props.viewportScale}
              viewportWidth={this.props.viewportWidth}
            />
          )}

        {this.state.isDeletePopupVisible && (
          <GenericPopup
            message={deleteMessage}
            onCancel={this.handleDeletePopupCancel}
            onConfirm={this.handleDeletePopupConfirm}
            severity="warning"
          />
        )}
        {showSecondaryControls && (
          <SecondaryControls
            controlMode="freetext"
            currentBgColor={annotation.bgColor}
            currentColor={annotation.color}
            currentFont={annotation.font}
            onCurrentBgColorChange={this.updateBgColor}
            onCurrentColorChange={this.updateColor}
            onCurrentTextSizeChange={this.updateTextSize}
          />
        )}
      </div>
    )
  }
}
